import { useState, useCallback } from "react";
import {
    createProjectApi,
    listProjectsApi,
    createTaskApi,
    listTasksApi,
    updateTaskApi,
    updateProjectApi,
    reorderTasksApi,
    createTaskGroupApi,
    updateTaskGroupApi,
    listTaskGroupsApi,
    deleteTaskGroupApi, // Added import
    assignTagApi,
    removeTagApi,
    deleteTaskApi,
    createReminderApi,
    deleteReminderApi
} from "../services/tasksService";

export function useTasks({ token, workspaceId }) {
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [taskGroups, setTaskGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    /* ---------------- PROJECTS ---------------- */

    const fetchProjects = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        setError("");
        try {
            const data = await listProjectsApi({ token, workspaceId });
            setProjects(Array.isArray(data) ? data : []);
            return data;
        } catch (err) {
            console.error(err);
            setError("Failed to load projects.");
        } finally {
            setLoading(false);
        }
    }, [token, workspaceId]);

    const createProject = async (data) => {
        setLoading(true);
        try {
            const newProject = await createProjectApi({ token, workspaceId, data });
            setProjects(prev => [newProject, ...prev]);
            return newProject;
        } catch (err) {
            console.error(err);
            setError("Failed to create project.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- TASKS & TASK GROUPS ---------------- */

    const fetchTasks = useCallback(async (projectId) => {
        if (!projectId) return;
        setLoading(true);
        setError("");
        try {
            const [tData, gData] = await Promise.all([
                listTasksApi({ token, workspaceId, projectId }),
                listTaskGroupsApi({ token, workspaceId, projectId })
            ]);
            setTasks(Array.isArray(tData) ? tData : []);
            setTaskGroups(Array.isArray(gData) ? gData : []);
        } catch (err) {
            console.error(err);
            setError("Failed to load tasks.");
        } finally {
            setLoading(false);
        }
    }, [token, workspaceId]);

    const createTask = async (data) => {
        setLoading(true);
        try {
            const newTask = await createTaskApi({ token, workspaceId, data });
            setTasks(prev => [newTask, ...prev]);
            return newTask;
        } catch (err) {
            console.error(err);
            setError("Failed to create task.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const updateTask = async (taskId, data) => {
        // Optimistic update logic
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...data } : t));

        try {
            const updatedTask = await updateTaskApi({ token, workspaceId, taskId, data });
            if (updatedTask && updatedTask.id) {
                setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updatedTask } : t));

                // Auto-refresh for recurring tasks completion to show next occurrence
                if (updatedTask.recurrence_rule && updatedTask.is_completed) {
                    // Small delay to ensure backend has created the next task
                    setTimeout(() => {
                        fetchTasks(updatedTask.project_id);
                    }, 500);
                }
            }
        } catch (err) {
            console.error(err);
            setError("Failed to update task.");
            // Rollback could be added here
            fetchTasks(data.projectId); // simple re-fetch strategy for sync issues
            throw err;
        }
    };

    const createTaskGroup = async (projectId, name) => {
        try {
            const newTaskGroup = await createTaskGroupApi({ token, workspaceId, data: { projectId, name } });
            setTaskGroups(prev => [...prev, newTaskGroup]);
            return newTaskGroup;
        } catch (err) {
            console.error("Failed to create task group");
            throw err;
        }
    };

    const updateTaskGroup = async (groupId, data) => {
        setTaskGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...data } : g));
        try {
            await updateTaskGroupApi({ token, workspaceId, groupId, data });
        } catch (err) {
            console.error("Failed to update task group");
        }
    };

    const reorderTaskGroups = async (reorderedGroups) => {
        // Optimistic: replace taskGroups with the new order
        setTaskGroups(reorderedGroups.map((g, idx) => ({ ...g, sort_order: idx })));
        // Persist each group's new sort_order
        try {
            await Promise.all(
                reorderedGroups.map((group, idx) =>
                    updateTaskGroupApi({ token, workspaceId, groupId: group.id, data: { sortOrder: idx } })
                )
            );
        } catch (err) {
            console.error("Failed to reorder task groups");
        }
    };

    const deleteTaskGroup = async (groupId) => {
        try {
            await deleteTaskGroupApi({ token, workspaceId, groupId });
            setTaskGroups(prev => prev.filter(g => g.id !== groupId));
            // Refresh tasks to ensure they are moved to 'uncategorized' or handled correctly
            if (projects.length > 0) {
                await fetchTasks(projects[0].id);
            }
        } catch (err) {
            console.error("Failed to delete task group", err);
            throw err;
        }
    };

    const updateProject = async (projectId, data) => {
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...data } : p));
        try {
            await updateProjectApi({ token, workspaceId, projectId, data });
        } catch (err) {
            console.error("Failed to update project");
        }
    };

    /* ---------------- TAGS - TASK OPERATIONS Only ---------------- */
    // Note: Tag creation/listing is now handled by useTaskTags hook.

    const assignTag = async (taskId, tag) => {
        if (!tag) return;

        // Optimistic Update
        setTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                const currentTags = t.tags || [];
                if (currentTags.find(ct => ct.id === tag.id)) return t;
                return { ...t, tags: [...currentTags, tag] };
            }
            return t;
        }));

        try {
            await assignTagApi({ token, workspaceId, taskId, tagId: tag.id });
        } catch (err) {
            console.error("Failed to assign tag", err);
            // Revert
            setTasks(prev => prev.map(t => {
                if (t.id === taskId) {
                    return { ...t, tags: (t.tags || []).filter(ct => ct.id !== tag.id) };
                }
                return t;
            }));
        }
    };

    const removeTag = async (taskId, tagId, tagToRestore) => {
        // Optimistic Update
        setTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                return { ...t, tags: (t.tags || []).filter(ct => ct.id !== tagId) };
            }
            return t;
        }));

        try {
            await removeTagApi({ token, workspaceId, taskId, tagId });
        } catch (err) {
            console.error("Failed to remove tag", err);
            // Revert
            if (tagToRestore) {
                setTasks(prev => prev.map(t => {
                    if (t.id === taskId) {
                        return { ...t, tags: [...(t.tags || []), tagToRestore] };
                    }
                    return t;
                }));
            }
        }
    };

    const deleteTask = async (taskId, mode = null) => {
        const previousTasks = [...tasks];

        // Optimistic Update
        if (mode === 'instance') {
            // For instance deletion, we don't remove it effectively immediately because it becomes 'cancelled'/'completed'
            // But we might want to hide it if user is looking at Open tasks.
            // Simplified: Just keep it until refresh or mark as completed locally?
            // Let's mark as completed locally for feedback.
            setTasks(prev => prev.map(t => t.id === taskId ? { ...t, is_completed: true } : t));
        } else {
            // Series or single delete
            setTasks(prev => prev.filter(t => t.id !== taskId));
        }

        try {
            await deleteTaskApi({ token, workspaceId, taskId, mode });

            // If instance deletion, we need to fetch again to get the NEW recurring instance
            if (mode === 'instance') {
                const task = previousTasks.find(t => t.id === taskId);
                if (task && task.project_id) {
                    // Delay slightly to allow backend to generate
                    setTimeout(() => fetchTasks(task.project_id), 500);
                }
            }

        } catch (err) {
            console.error("Failed to delete task", err);
            // Revert
            setTasks(previousTasks);
        }
    };

    /* ---------------- REMINDERS ---------------- */

    const addReminder = async (taskId, remindAt) => {
        // 1. Optimistic Update
        const tempId = 'temp-' + Date.now();
        setTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                const newReminder = { id: tempId, remind_at: remindAt };
                return { ...t, reminders: [...(t.reminders || []), newReminder] };
            }
            return t;
        }));

        try {
            await createReminderApi({ token, workspaceId, taskId, remindAt });

            // 2. Re-fetch to get real ID and consistent state.
            const task = tasks.find(t => t.id === taskId);
            if (task && task.project_id) {
                fetchTasks(task.project_id);
            }
        } catch (err) {
            console.error("Failed to add reminder", err);
            // Revert
            setTasks(prev => prev.map(t => {
                if (t.id === taskId) {
                    return { ...t, reminders: (t.reminders || []).filter(r => r.id !== tempId) };
                }
                return t;
            }));
        }
    };

    const deleteReminder = async (taskId, reminderId) => {
        // 1. Optimistic Update
        const previousTasks = [...tasks];
        setTasks(prev => prev.map(t => {
            if (t.id === taskId) {
                return { ...t, reminders: (t.reminders || []).filter(r => r.id !== reminderId) };
            }
            return t;
        }));

        try {
            await deleteReminderApi({ token, workspaceId, taskId, reminderId });
        } catch (err) {
            console.error("Failed to delete reminder", err);
            // Revert
            setTasks(previousTasks);
        }
    }

    const reorderTasks = async (updates) => {
        // Optimistic update
        setTasks(prev => {
            const newTasks = [...prev];
            updates.forEach(u => {
                const idx = newTasks.findIndex(t => t.id === u.id);
                if (idx !== -1) {
                    newTasks[idx] = { ...newTasks[idx], sort_order: u.sortOrder, section_id: u.taskGroupId };
                }
            });
            return newTasks.sort((a, b) => {
                if (a.is_completed !== b.is_completed) {
                    return a.is_completed ? 1 : -1;
                }
                return (a.sort_order || 0) - (b.sort_order || 0);
            });
        });

        try {
            await reorderTasksApi({ token, workspaceId, updates });
        } catch (err) {
            console.error("Failed to reorder tasks");
            fetchTasks(projects.length > 0 ? projects[0].id : null);
        }

    };

    return {
        projects,
        tasks,
        taskGroups,
        loading,
        error,
        fetchProjects,
        createProject,
        fetchTasks,
        createTask,
        updateTask,
        reorderTasks,
        createTaskGroup,
        assignTag,
        removeTag,
        deleteTask,
        addReminder,
        deleteReminder,
        updateProject,
        updateTaskGroup,
        reorderTaskGroups,
        deleteTaskGroup
    };
}

